/**
 * AbsencesTable — Tabelle mit Filter + leerem Zustand.
 *
 * 2026-Feb: Spalte „Nachweis" zeigt jetzt zusaetzlich AUB/eAUB-Badge bei K und
 * eine Paperclip-Pille mit Anzahl der hochgeladenen Dateien (klickbar →
 * oeffnet die erste Datei; weitere via Edit).
 */
import React from 'react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  FileText, MoreHorizontal, Pencil, Trash2, UserX, Plus, Paperclip,
} from 'lucide-react';
import { getAbsenceType, getCertificateType } from './constants';
import { openAuthFile } from '../../lib/openAuthFile';

const renderProof = (absence) => {
  const items = [];
  if (absence.absence_type === 'K') {
    const ct = getCertificateType(absence.certificate_type);
    if (ct) {
      items.push(
        <Badge key="ct" className={ct.color} data-testid={`absence-cert-badge-${absence.id}`}>
          {ct.short}
          {absence.doctor_note_date && ct.value !== 'none' &&
            ` · ${format(parseISO(absence.doctor_note_date), 'dd.MM.', { locale: de })}`}
        </Badge>
      );
    } else if (absence.doctor_note) {
      // Backward-compat: alte Eintraege ohne certificate_type
      items.push(
        <Badge key="legacy" className="bg-green-100 text-green-800">
          Attest{absence.doctor_note_date && ` (${format(parseISO(absence.doctor_note_date), 'dd.MM.', { locale: de })})`}
        </Badge>
      );
    }
  } else if (absence.absence_type === 'KK' && absence.child_sick_certificate) {
    items.push(<Badge key="kk" className="bg-green-100 text-green-800">Attest</Badge>);
  } else if (absence.absence_type === 'EZ' && absence.employer_confirmation) {
    items.push(<Badge key="ez" className="bg-green-100 text-green-800">AG-Best.</Badge>);
  }

  const attachments = absence.attachments || [];
  if (attachments.length > 0) {
    items.push(
      <button
        key="att"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          openAuthFile(attachments[0].url, { filename: attachments[0].original_filename })
            .catch(() => { /* ignore */ });
        }}
        className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border border-border bg-secondary/40 hover:bg-secondary transition-colors"
        title={attachments.map((a) => a.original_filename).join('\n')}
        data-testid={`absence-attachments-badge-${absence.id}`}
      >
        <Paperclip className="h-3 w-3" />
        {attachments.length}
      </button>
    );
  }

  if (items.length === 0) return <span className="text-muted-foreground">-</span>;
  return <div className="flex flex-wrap gap-1.5 items-center">{items}</div>;
};

export const AbsencesTable = ({ rows, year, onEdit, onDelete, onCreate }) => (
  <Card className="border-border/60">
    <CardHeader className="border-b border-border/40">
      <CardTitle className="font-heading flex items-center gap-2">
        <FileText className="h-5 w-5 text-primary" />
        Abwesenheitsliste ({rows.length} Einträge)
      </CardTitle>
    </CardHeader>
    <CardContent className="p-0">
      {rows.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow className="data-table">
              <TableHead>Typ</TableHead>
              <TableHead>Mitarbeiter</TableHead>
              <TableHead>Zeitraum</TableHead>
              <TableHead className="text-center">Tage</TableHead>
              <TableHead>Nachweis</TableHead>
              <TableHead>Erfasst von</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((absence, index) => {
              const type = getAbsenceType(absence.absence_type);
              return (
                <TableRow
                  key={absence.id}
                  className="animate-slide-up"
                  style={{ animationDelay: `${index * 0.02}s` }}
                  data-testid={`absence-row-${absence.id}`}
                >
                  <TableCell>
                    <Badge className={type.color}>
                      <type.icon className="h-3 w-3 mr-1" />
                      {type.short}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium">{absence.employee_name}</p>
                      <p className="text-xs text-muted-foreground">{absence.employee_number}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">
                      {format(parseISO(absence.start_date), 'dd.MM.yyyy', { locale: de })}
                      {absence.start_date !== absence.end_date && (
                        <> - {format(parseISO(absence.end_date), 'dd.MM.yyyy', { locale: de })}</>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary">{absence.days_count}</Badge>
                  </TableCell>
                  <TableCell>{renderProof(absence)}</TableCell>
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{absence.created_by_name}</span>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEdit(absence)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          Bearbeiten
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onDelete(absence.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Löschen
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center py-12">
          <UserX className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="font-medium text-lg">Keine Abwesenheiten</h3>
          <p className="text-muted-foreground mt-1">
            Für {year} wurden noch keine Abwesenheiten erfasst.
          </p>
          <Button className="mt-4 rounded-full" onClick={onCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Erste Abwesenheit erfassen
          </Button>
        </div>
      )}
    </CardContent>
  </Card>
);
